import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnType } from 'antd/lib/table/interface';
import { v4 as uuidv4 } from 'uuid';

import IbButton from '../IbButton';
import IbIcon from '../IbIcon';
import IbInput from '../IbInput';
import IbTable from '../IbTable';

interface IHeaderRow {
  id: string;
  key: string;
  value: string;
}

const toRows = (headers: Record<string, string>): IHeaderRow[] =>
  Object.entries(headers).map(([key, value]) => ({ id: uuidv4(), key, value }));

const toRecord = (rows: IHeaderRow[]): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const row of rows) {
    if (row.key.trim()) {
      result[row.key] = row.value;
    }
  }
  return result;
};

const areHeadersEqual = (left: Record<string, string>, right: Record<string, string>) => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => left[key] === right[key]);
};

export interface IHttpHeadersEditorProps {
  className?: string;
  value?: Record<string, string>;
  onChange?: (value: Record<string, string>) => void;
}

const HttpHeadersEditor: React.FC<IHttpHeadersEditorProps> = ({ className, value = {}, onChange }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<IHeaderRow[]>(() => toRows(value));
  const rowsRef = useRef(rows);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    if (!areHeadersEqual(toRecord(rowsRef.current), value)) {
      setRows(toRows(value));
    }
  }, [value]);

  const updateRows = (newRows: IHeaderRow[]) => {
    setRows(newRows);
    onChange?.(toRecord(newRows));
  };

  const onKeyChange = (id: string, newKey: string) => {
    updateRows(rows.map((r) => (r.id === id ? { ...r, key: newKey } : r)));
  };

  const onValueChange = (id: string, newValue: string) => {
    updateRows(rows.map((r) => (r.id === id ? { ...r, value: newValue } : r)));
  };

  const onDeleteRow = (id: string) => {
    updateRows(rows.filter((r) => r.id !== id));
  };

  const onAddRow = () => {
    updateRows([...rows, { id: uuidv4(), key: '', value: '' }]);
  };

  const columns: ColumnType<IHeaderRow>[] = [
    {
      title: t('HTTP headers editor: Header name: Title'),
      render: (_, row) => (
        <IbInput
          placeholder={t('HTTP headers editor: Header name: Placeholder')}
          value={row.key}
          onChange={(val) => onKeyChange(row.id, val)}
        />
      ),
      width: '40%',
    },
    {
      title: t('HTTP headers editor: Header value'),
      render: (_, row) => (
        <IbInput
          placeholder={t('HTTP headers editor: Header value placeholder')}
          value={row.value}
          onChange={(val) => onValueChange(row.id, val)}
        />
      ),
    },
    {
      title: '',
      render: (_, row) => (
        <IbButton icon={<IbIcon iconName="delete" />} type="icon" onClick={() => onDeleteRow(row.id)} />
      ),
      width: '48px',
    },
  ];

  return (
    <div className={className}>
      <IbTable columns={columns} dataSource={rows} rowKey="id" size="small" />
      <IbButton icon={<IbIcon iconName="add-one" />} type="link" onClick={onAddRow}>
        {t('HTTP headers editor: Add header')}
      </IbButton>
    </div>
  );
};

export default HttpHeadersEditor;
